export const isTauriRuntime = (): boolean => {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
};

/**
 * A window's label is how it says what it is: the kind is decoded from it here and nowhere else, so
 * a new kind of window costs one line below rather than an edit in every place that cares.
 */
export const MAIN_WINDOW_LABEL = "main";
/** One surface — a document workspace, an agent, a popped-out chat — rather than the application. */
export const SURFACE_WINDOW_LABEL_PREFIX = "detached-";
/** The whole application again, at its own route: what cmd+clicking a link opens. */
export const APPLICATION_WINDOW_LABEL_PREFIX = "window-";

export type DesktopWindowKind = "main" | "application" | "surface";

/** The Tauri label of the window this document is running in; null anywhere it has none. */
export const currentWindowLabel = (): string | null => {
  if (!isTauriRuntime()) return null;
  try {
    // Read synchronously off the runtime metadata rather than through `getCurrentWindow()`, which
    // this module cannot import without pulling the Tauri API into every web bundle that touches it.
    return (
      (window as { __TAURI_INTERNALS__?: { metadata?: { currentWindow?: { label?: string } } } })
        .__TAURI_INTERNALS__?.metadata?.currentWindow?.label ?? null
    );
  } catch {
    return null;
  }
};

/**
 * What this window is. The browser, which has only one, is always the main window.
 *
 * Ask this rather than testing a label: the kinds are a closed set decoded in one place, and code
 * that branches on a label of its own is what makes each new kind of window a special case.
 */
export const currentWindowKind = (): DesktopWindowKind => {
  const label = currentWindowLabel();
  if (!label || label === MAIN_WINDOW_LABEL) return "main";
  if (label.startsWith(SURFACE_WINDOW_LABEL_PREFIX)) return "surface";
  return "application";
};

/**
 * Whether this document is the window that owns the application as a whole.
 *
 * False in a surface window and in the extra application windows a cmd+clicked link opens. Anything
 * that belongs to the app rather than to the page on screen — deal notifications, the updater —
 * should happen in one window only.
 */
export const isMainWindow = (): boolean => currentWindowKind() === "main";
